import * as React from 'react';

import { withTranslation, WithTranslation } from 'react-i18next';

import ReactTable, { Column, FinalState, RowInfo } from 'react-table';
import {
    getAllReportTemplates,
    getDefaultReports,
    setSelectedDefaultReport,
    setSelectedReport,
    setTableFilter
} from '../../actions/manageReportActions';
import {
    Ifacility,
    IreportTemplate,
    Itile,
    Ioption,
    IproductInfo
} from '../../models';
import { emptyTile, initialFacility } from '../../reducers/initialState';

import { Banner } from '../common/Banner';
/*
 * Manage Reports
 */
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { getJobsByFacility } from '../../actions/manageJobActions';
import { getProductInfo as getProductInfoAction } from '../../actions/manageInventoryActions';
import { constants } from '../../constants/constants';
import { jobTypesIdEnumInverse } from '../../models-enums';
import { IinitialState } from '../../reducers';
import { selectIsLoading } from '../../reducers/commonReducers';
import { getProductInfo } from '../../reducers/productInfoReducer';
import { getCanAccessAllCustomers } from '../../reducers/userReducer';
import { TableUtil } from '../common/TableUtil';
import ManageReportModal from './ManageReportModal';
import ManageReportFilter from '../manageReportBuilder/ManageReportFilter';

interface RowInfoReport extends RowInfo {
    original: IreportTemplate;
}

interface Iprops extends RouteComponentProps<any> {
    // Add your regular properties here
    loading: boolean;
}

interface IdispatchProps {
    // Add your dispatcher properties here
    setSelectedReport: typeof setSelectedReport;
    setSelectedDefaultReport: typeof setSelectedDefaultReport;
    getJobsByFacility: typeof getJobsByFacility;
    getProductInfoAction: typeof getProductInfoAction;
    selectedFacility: Ifacility;
    canAccessAllCustomers: boolean;
    productInfo: IproductInfo;
}

const ManageReport: React.FC<Iprops &
    IdispatchProps &
    WithTranslation> = props => {
    const { t, productInfo } = props;
    const [columns, setColumns] = React.useState<any[]>([]);
    const [selectedRow, setSelectedRow] = React.useState<any>(null);
    const [currentTile, setCurrentTile] = React.useState<Itile>(emptyTile);
    const [selectedTemplate, setSelectedTemplate] = React.useState<
        IreportTemplate | undefined
    >(undefined);
    const [showManageReportModal, setShowManageReportModal] = React.useState<
        boolean
    >(false);

    const [reportTemplates, setReportTemplates] = React.useState<
        IreportTemplate[]
    >([]);

    // Filter values state
    const [filterValues, setFilterValues] = React.useState<{
        brand: string | null;
        jobType: string | null;
        default: boolean | null;
    }>({
        brand: null,
        jobType: null,
        default: null
    });

    // Filtered templates based on filter values
    const [filteredTemplates, setFilteredTemplates] = React.useState<
        IreportTemplate[]
    >([]);

    // Function to fetch templates
    const fetchTemplates = async () => {
        const fetchedTemplates = await getAllReportTemplates();
        setReportTemplates(fetchedTemplates);
    };

    // Filter change handler
    const handleFilterChange = (formValues: {
        brand?: Ioption | null;
        jobType?: Ioption | null;
        default?: Ioption | null;
    }) => {
        let convertedDefault: boolean | null = null;
        if (
            formValues.default?.value !== null &&
            formValues.default?.value !== undefined
        ) {
            convertedDefault = formValues.default.value as any;
        }

        setFilterValues({
            brand: formValues.brand?.value || null,
            jobType: formValues.jobType?.value || null,
            default: convertedDefault
        });
    };

    // Apply filters to templates
    React.useEffect(() => {
        let filtered = [...reportTemplates];

        if (filterValues.brand) {
            filtered = filtered.filter(
                template => template.brandID === filterValues.brand
            );
        }

        if (filterValues.jobType) {
            filtered = filtered.filter(
                template => template.jobTypeID === filterValues.jobType
            );
        }

        if (filterValues.default !== null) {
            filtered = filtered.filter(
                template => template.isDefault === filterValues.default
            );
        }

        setFilteredTemplates(filtered);
    }, [reportTemplates, filterValues]);

    React.useEffect(() => {
        setCurrentTile(constants.getTileByURL(props.location.pathname));
        setColumns(
            TableUtil.translateHeaders(
                [
                    {
                        Header: t('manageReportBuilder:FilterTemplateName'),
                        accessor: 'templateName' // Ensure this matches the key in your data
                    },
                    {
                        id: 'jobType',
                        Header: t('manageWorkOrder:jobType'),
                        accessor: (row: IreportTemplate) => {
                            return props.t(
                                `nsJob:${
                                    jobTypesIdEnumInverse[
                                        row.jobTypeID as keyof typeof jobTypesIdEnumInverse
                                    ]
                                }`
                            );
                        }
                    },
                    {
                        id: 'brand',
                        Header: t('common:brand'),
                        accessor: (row: IreportTemplate) => {
                            return productInfo?.allBrands[row.brandID]?.name;
                        }
                    },
                    {
                        Header: t('manageReportBuilder:HeaderIsDefault'),
                        accessor: 'isDefault', // Ensure this matches the key in your data
                        Cell: ({ value }: { value: boolean }) => (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <span>{value ? 'Yes' : 'No'}</span>
                            </div>
                        )
                    }
                ],
                t
            )
        );

        fetchTemplates();
        props.getJobsByFacility();
        props.getProductInfoAction(); // Fetch product info when the component mounts
        // eslint-disable-next-line
    }, [props.t, props.i18n.language]);

    React.useEffect(() => {
        if (!showManageReportModal) {
            setSelectedRow(null);
        }
    }, [showManageReportModal]);

    React.useEffect(() => {
        props.getJobsByFacility();
        // eslint-disable-next-line
    }, [props.selectedFacility.id]);

    /*
     * (reusable)
     * set the row color after a user selects it
     */
    const getTrProps = (state: FinalState, rowInfo: RowInfo | undefined) => {
        if (rowInfo) {
            return {
                style: {
                    background:
                        rowInfo.index === selectedRow
                            ? constants.colors[
                                  `${currentTile.color}Tr` as keyof typeof constants.colors
                              ]
                            : ''
                }
            };
        } else {
            return {};
        }
    };
    /*
     * (reusable)
     * get the next or previous page of data.  the table is 0 indexed but the API is not
     */
    const onPageChange = (page: number) => {
        // const newPage = page + 1;
        // props.setTableFilter({ page: newPage });
    };

    const getTdProps = (
        fState: FinalState,
        rowInfo: RowInfoReport | undefined,
        column: Column | undefined,
        instance: any
    ) => {
        if (rowInfo) {
            return {
                onClick: () => {
                    setSelectedRow(rowInfo.index);
                    setSelectedTemplate(rowInfo.original);
                    // props.setSelectedDefaultReport(rowInfo.original.id);
                    setShowManageReportModal(true);
                }
            };
        } else {
            return {};
        }
    };

    const availableBrandOptions = React.useMemo(() => {
        const brandIDs = new Set(reportTemplates.map(t => t.brandID));
        return (productInfo.allBrandOptions || []).filter(opt =>
            brandIDs.has(opt.value)
        );
    }, [reportTemplates, productInfo.allBrandOptions]);

    return (
        <div className="manage-job">
            <Banner
                title={t('bannerTitle')}
                img={currentTile.srcBanner}
                color={currentTile.color}
            />

            <ManageReportFilter
                brandOptions={availableBrandOptions}
                setFormGroup={handleFilterChange} // Update filter values in parent state
            />

            <ReactTable
                data={filteredTemplates}
                columns={columns}
                getTrProps={getTrProps}
                pageSize={
                    filteredTemplates.length >= 10
                        ? filteredTemplates.length
                        : 10
                }
                showPageSizeOptions={false}
                className={`beacon-table -highlight ${currentTile.color}`}
                previousText={t('common:previous')}
                nextText={t('common:next')}
                onPageChange={onPageChange}
                sortable={false}
                noDataText={t('common:noDataText')}
                resizable={false}
                getTdProps={getTdProps}
                pageText={t('common:pageText')}
            />
            <ManageReportModal
                selectedTemplate={selectedTemplate}
                forDailyRounds={false}
                className="first-modal"
                show={showManageReportModal}
                title={
                    selectedTemplate
                        ? selectedTemplate.templateName
                        : 'Daily Rounds Report'
                }
                closeModal={() => setShowManageReportModal(false)}
                onHide={() => setShowManageReportModal(false)}
            />
        </div>
    );
};

const mapStateToProps = (state: IinitialState, ownProps: Iprops) => {
    const selectedFacility =
        state.facilities[state.selectedFacilityID] || initialFacility;
    const canAccessAllCustomers = getCanAccessAllCustomers(state.user);
    const productInfo = getProductInfo(state);

    return {
        user: state.user,
        loading: selectIsLoading(state),
        selectedFacility,
        canAccessAllCustomers,
        productInfo
    };
};
export default withTranslation('manageReport')(
    connect(mapStateToProps, {
        getDefaultReports,
        setTableFilter,
        setSelectedReport,
        setSelectedDefaultReport,
        getJobsByFacility,
        getProductInfoAction
    })(ManageReport)
);
